import React, { Context, ReactNode, useState, useRef, useEffect } from 'react';
import { createPortal } from 'react-dom';
import { uuid } from 'uuidv4';
import { useDeepCompareCallback } from 'use-deep-compare';

import { Modal } from './components/Modal';

interface Props {
    children?: ReactNode;
    ModalContext: Context<ModalProviderValue>;
    modalRootId: string;
}

export const ModalProvider = ({
    children,
    ModalContext,
    modalRootId,
}: Props): JSX.Element => {
    const hasOpenModalRef = useRef(false);
    const [modals, setModals] = useState([] as ModalType<unknown>[]);

    let modalRoot = document.getElementById(modalRootId);

    if (modalRoot === null) {
        modalRoot = document.body;
    }

    const removeModal = useDeepCompareCallback(
        (id: string, removeModalInput: RemoveModalInput): void => {
            const targetModal = modals.find(
                (modal: ModalType<unknown>): boolean => modal.id === id,
            );

            if (targetModal) {
                if (targetModal.handleClose) {
                    targetModal.handleClose(removeModalInput);
                }

                setModals(
                    (prevModals: ModalType<unknown>[]): ModalType<unknown>[] =>
                        prevModals.filter(
                            (modal: ModalType<unknown>): boolean =>
                                modal.id !== id,
                        ),
                );
            }
        },
        [modals],
    );

    const addModal: AddModal = useDeepCompareCallback(
        ({
            id = uuid(),
            Content,
            handleClose,
            input = {},
        }: AddModalInput<unknown>): string => {
            // If an id is provided, only create a new modal if a modal with the provided id does not
            // exist yet. This will be useful if modal.add() is triggered by React reducer - see
            // matching pattern in packages/components/src/Toast/ToastProvider.tsx.
            if (
                !modals.some(
                    (modal: ModalType<unknown>): boolean => modal.id === id,
                )
            ) {
                setModals(
                    (
                        prevModals: ModalType<unknown>[],
                    ): ModalType<unknown>[] => [
                        ...prevModals,
                        { id, Content, handleClose, input },
                    ],
                );
            }

            return id;
        },
        [modals],
    );

    useEffect(() => {
        if (modals.length > 0) {
            hasOpenModalRef.current = true;
        } else {
            hasOpenModalRef.current = false;
        }
    }, [modals.length]);

    return (
        <ModalContext.Provider
            value={{
                add: addModal,
                hasOpenModalRef,
                remove: removeModal,
            }}
        >
            {createPortal(
                <div className="modal">
                    {modals.map(
                        ({
                            id,
                            Content,
                            input,
                        }: ModalType<unknown>): JSX.Element => (
                            <Modal<unknown>
                                key={id}
                                Content={Content}
                                input={input}
                                remove={(
                                    removeModalInput: RemoveModalInput,
                                ): void => removeModal(id, removeModalInput)}
                            />
                        ),
                    )}
                </div>,
                modalRoot,
            )}
            {children}
        </ModalContext.Provider>
    );
};
